/* eslint-disable max-len */
/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable id-length */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import SearchDropdown from '../../../../Components/Dropdown/dropdown';
import { Dialog } from 'primereact/dialog';
import './ChangeRequestDialog.scss';
import { useAuth } from '../../../../Hooks/useAuth';
import { toast } from 'react-toastify';
import Loader from '../../../../Components/Loader';
import { createPortal } from 'react-dom';
import DialogSkeleton from './DialogSkeleton';
import PropTypes from 'prop-types';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  fetchCROption,
  fetchCRDetails,
  createChangeRequest,
  updateChangeRequest,
} from '../../../../Api/request/projectChangeReq';

const emptyEffortForm = () => ({
  resourceCategory: '',
  resourceCategoryLabel: '',
  resourceSkills: '',
  resourceSkillsLabel: '',
  effortHrs: '',
  details: '',
});

const EMPTY_FORM = {
  projectName: '',
  crName: '',
  crDescription: '',
  remarks: '',
  effortsInvestment: '',
  customerApprovalStatus: '',
  droppedStatus: '',
};

const LIMITS = {
  CR_NAME: 100,
  DESCRIPTION: 250,
  REMARKS: 200,
  DETAILS: 150,
  EFFORT_HOURS_MAX: 9999,
};

const resolveDropdownValue = (optionsList = [], id, knownLabel = '') => {
  if (id === null || id === undefined || id === '') return '';
  if (typeof id === 'object') return id;

  const byId = optionsList.find((opt) => String(opt.value ?? opt.id ?? opt) === String(id));
  if (byId) return byId;

  const byLabel = knownLabel
    ? optionsList.find(
      (opt) => String(opt.label ?? opt.name ?? opt).toLowerCase() === String(knownLabel).toLowerCase(),
    )
    : null;
  if (byLabel) return byLabel;

  if (knownLabel) {
    return { label: knownLabel, name: knownLabel, value: id, id };
  }

  return '';
};

const getOptionLabel = (optionsList = [], val, knownLabel = '') => {
  if (val === null || val === undefined || val === '') return '-';
  const found = optionsList.find(
    (opt) => String(opt.value ?? opt.id ?? opt) === String(val),
  );
  if (found) return found.label ?? found.name ?? String(found);
  return knownLabel || String(val);
};

const ChangeRequestDialog = ({
  visible,
  onHide,
  rowData,
  isCreate = false,
  onSuccess,
}) => {
  const { userProfile } = useAuth();
  const token = useMemo(() => userProfile?.access_token || '', [userProfile]);

  const [resourceCategoryOptions, setResourceCategoryOptions] = useState([]);
  const [resourceSkillsOptions, setResourceSkillsOptions] = useState([]);
  const [effortInvestmentOptions, setEffortInvestmentOptions] = useState([]);
  const [projectNameOptions, setProjectNameOptions] = useState([]);
  const [customerApprovalOptions, setCustomerApprovalOptions] = useState([]);
  const [filteredCustomerApprovalOptions, setFilteredCustomerApprovalOptions] = useState([]);
  const [initialState, setInitialState] = useState(null);

  const [optionsLoading, setOptionsLoading] = useState(false);

  const [crDetail, setCrDetail] = useState(null);
  const [crStatusVisible, setCrStatusVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [effortRows, setEffortRows] = useState([]);
  const [currentEffort, setCurrentEffort] = useState(emptyEffortForm());
  const [editingRowId, setEditingRowId] = useState(null);
  const [crDetailLoading, setcrDetailLoading] = useState(false);

  const optionsAbortRef = useRef(null);

  const getCurrentState = () => ({
    form,
    efforts: effortRows.map((r) => ({
      category_id: Number(r.resourceCategory) || null,
      type_id: Number(r.resourceSkills) || null,
      efforts_hours: parseFloat(r.effortHrs) || 0,
      details: r.details ?? '',
    })),
  });

  const isProjectDisabled = useMemo(() => {
    if (submitting || optionsLoading) return true;
    if (!isCreate) return true;
    return Boolean(
      rowData?.projectId
      || rowData?.projectName
      || form.projectName,
    );
  }, [submitting, optionsLoading, isCreate, rowData, form.projectName]);

  useEffect(() => {
    if (!visible) return;

    const controller = new AbortController();
    optionsAbortRef.current = controller;

    const fetchOption = async (optionType, setter) => {
      try {
        const userInfo = {
          userName: userProfile?.user_name || 'unknown',
          roleDetails: userProfile?.roleDetails || 'unknown',
          emailId: userProfile?.email || 'unknown',
          employeeId: userProfile?.employeeId || 'unknown',
        };

        const res = await fetchCROption({ authToken: token, optionType, userInfo });
        const json = res?.data;

        if (json?.success && Array.isArray(json.data)) {
          const formattedData = json.data.map((item) => {
            if (typeof item === 'object' && item !== null) {
              const lbl = item.label ?? item.name ?? String(item.value ?? item.id ?? '');
              const val = item.value ?? item.id ?? item;
              return { label: lbl, name: lbl, value: val, id: val };
            }
            return { label: String(item), name: String(item), value: item, id: item };
          });
          setter(formattedData);
        }
      } catch (err) {
        toast.error(`Failed to fetch ${optionType}`);
      }
    };

    if (!resourceCategoryOptions.length) {
      setOptionsLoading(true);
      Promise.allSettled([
        fetchOption('resource-category', setResourceCategoryOptions),
        fetchOption('resource-type', setResourceSkillsOptions),
        fetchOption('efforts-investment-types', setEffortInvestmentOptions),
        fetchOption('project-names', setProjectNameOptions),
        fetchOption('customer-approval-statuses', setCustomerApprovalOptions),
      ]).then(() => {
        setOptionsLoading(false);
      });
    }

    return () => controller.abort();
  }, [visible, token]);

  useEffect(() => {
    if (!visible || isCreate || !rowData) {
      setCrDetail(null);
      setcrDetailLoading(false);
      return;
    }

    const crId = rowData.cr_id ?? rowData.crId;
    if (!crId) return;

    setcrDetailLoading(true);

    fetchCRDetails({ authToken: token, crId })
      .then((res) => {
        const json = res?.data;
        // Fix: Extract json.data.current if present
        if (json?.success && json.data) {
          setCrDetail(json.data.current ?? json.data);
        }
      })
      .catch(() => {
        toast.error('Failed to fetch Change Request details');
      })
      .finally(() => setcrDetailLoading(false));
  }, [visible, rowData, token, isCreate]);

  useEffect(() => {
    if (visible && isCreate) {
      const selectedProject
        = rowData?.projectId
        ?? rowData?.project_id
        ?? rowData?.projectName
        ?? '';

      setForm({
        ...EMPTY_FORM,
        projectName: selectedProject,
      });
      setEffortRows([]);
      setCurrentEffort(emptyEffortForm());
      setEditingRowId(null);
    }
  }, [visible, isCreate, rowData]);

  useEffect(() => {
    if (!crDetail) return;

    const savedStatus = crDetail.cr_status_value ?? '';
    const normalizedDroppedStatus
      = savedStatus.toLowerCase() === 'dropped'
        ? 'Dropped'
        : rowData?.crStatus ?? savedStatus;

    const rawStatusId = crDetail.customer_approved_status ?? crDetail.customer_ap_status ?? '';
    const rawStatusLabel = crDetail.customer_ap_value ?? '';

    // Match numerical ID or label string safely
    const matchedOption = customerApprovalOptions.find(
      (opt) => String(opt.value ?? opt.id ?? opt) === String(rawStatusId),
    ) ?? customerApprovalOptions.find(
      (opt) =>
        String(opt.label ?? opt.name ?? opt).toLowerCase() === String(rawStatusLabel).toLowerCase(),
    );

    const initialForm = {
      projectName:
        crDetail.project_id
        ?? crDetail.projectId
        ?? rowData?.projectId
        ?? rowData?.project_id
        ?? '',
      crName: crDetail.account_name ?? '',
      crDescription: crDetail.description ?? '',
      remarks: crDetail.remarks ?? '',
      effortsInvestment: crDetail.efforts_investment ?? '',
      customerApprovalStatus: matchedOption ? (matchedOption.value ?? matchedOption.id) : rawStatusId,
      droppedStatus: normalizedDroppedStatus,
    };

    const initialEfforts = crDetail.effort_rows?.length
      ? crDetail.effort_rows.map((r) => ({
        category_id: Number(r.category_id) || null,
        type_id: Number(r.type_id) || null,
        efforts_hours: parseFloat(r.efforts_hours) || 0,
        details: r.details ?? '',
      }))
      : [];

    setForm(initialForm);
    setEffortRows(
      crDetail.effort_rows?.length
        ? crDetail.effort_rows.map((r) => ({
          id: r.effort_id || `${Date.now()}-${Math.random()}`,
          resourceCategory: Number(r.category_id) || '',
          resourceCategoryLabel: r.resource_category ?? '',
          resourceSkills: Number(r.type_id) || '',
          resourceSkillsLabel: r.resource_type ?? '',
          effortHrs: r.efforts_hours ?? '',
          details: r.details ?? '',
        }))
        : [],
    );
    setCurrentEffort(emptyEffortForm());
    setEditingRowId(null);
    setInitialState({
      form: initialForm,
      efforts: initialEfforts,
    });
  }, [crDetail, rowData, customerApprovalOptions]);

  useEffect(() => {
    if (!customerApprovalOptions.length) return;

    const invVal = typeof form.effortsInvestment === 'object'
      ? form.effortsInvestment?.value
      : form.effortsInvestment;

    const isYes = String(invVal ?? '').toLowerCase() === 'y' || String(invVal ?? '').toLowerCase() === 'yes';
    setCrStatusVisible(isYes);

    const filtered = isYes
      ? customerApprovalOptions.filter((opt) => {
        const label = String(opt.label ?? opt.name ?? opt).toLowerCase();
        return label === 'rejected' || label === 'not applicable';
      })
      : customerApprovalOptions.filter((opt) => {
        const label = String(opt.label ?? opt.name ?? opt).toLowerCase();
        return label !== 'not applicable';
      });

    setFilteredCustomerApprovalOptions(filtered);

    const stillValid = filtered.some(
      (opt) => String(opt.value ?? opt.id ?? opt) === String(form.customerApprovalStatus),
    );

    if (form.customerApprovalStatus && !stillValid) {
      setForm((prev) => ({ ...prev, customerApprovalStatus: '' }));
    }
  }, [form.effortsInvestment, customerApprovalOptions, form.customerApprovalStatus]);

  const projectNameDropdownValue = useMemo(
    () => resolveDropdownValue(
      projectNameOptions,
      form.projectName,
      crDetail?.project_name ?? rowData?.projectName ?? '',
    ),
    [projectNameOptions, form.projectName, crDetail, rowData],
  );

  const resourceCategoryDropdownValue = useMemo(
    () => resolveDropdownValue(
      resourceCategoryOptions,
      currentEffort.resourceCategory,
      currentEffort.resourceCategoryLabel,
    ),
    [resourceCategoryOptions, currentEffort.resourceCategory, currentEffort.resourceCategoryLabel],
  );

  const resourceSkillsDropdownValue = useMemo(
    () => resolveDropdownValue(
      resourceSkillsOptions,
      currentEffort.resourceSkills,
      currentEffort.resourceSkillsLabel,
    ),
    [resourceSkillsOptions, currentEffort.resourceSkills, currentEffort.resourceSkillsLabel],
  );

  const effortsInvestmentDropdownValue = useMemo(
    () => resolveDropdownValue(effortInvestmentOptions, form.effortsInvestment),
    [effortInvestmentOptions, form.effortsInvestment],
  );

  const customerApprovalDropdownValue = useMemo(
    () => resolveDropdownValue(
      filteredCustomerApprovalOptions,
      form.customerApprovalStatus,
      crDetail?.customer_ap_value ?? '',
    ),
    [filteredCustomerApprovalOptions, form.customerApprovalStatus, crDetail],
  );

  const totalEffort = useMemo(
    () =>
      effortRows.reduce((sum, r) => {
        const val = parseFloat(r.effortHrs);
        return sum + (isNaN(val) ? 0 : val);
      }, 0),
    [effortRows],
  );

  const handleFormChange = useCallback((field, rawValue) => {
    const value = (rawValue && typeof rawValue === 'object' && 'value' in rawValue)
      ? rawValue.value
      : rawValue;

    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'effortsInvestment' && { customerApprovalStatus: '' }),
    }));
  }, []);

  const handleCurrentEffortChange = useCallback((field, rawValue) => {
    const isObject = rawValue && typeof rawValue === 'object' && 'value' in rawValue;
    const value = isObject ? rawValue.value : rawValue;
    const pickedLabel = isObject ? (rawValue.label ?? rawValue.name ?? '') : '';

    setCurrentEffort((prev) => {
      let updatedVal = value;
      if (field === 'effortHrs') {
        let val = parseFloat(updatedVal);
        if (isNaN(val)) val = '';
        if (val > LIMITS.EFFORT_HOURS_MAX) val = LIMITS.EFFORT_HOURS_MAX;
        if (val < 0) val = 0;
        updatedVal = val;
      }
      if (field === 'details' && updatedVal && updatedVal.length > LIMITS.DETAILS) {
        updatedVal = updatedVal.slice(0, LIMITS.DETAILS);
      }

      const next = {
        ...prev,
        [field]: field === 'resourceCategory' || field === 'resourceSkills'
          ? (updatedVal ? Number(updatedVal) : '')
          : updatedVal,
      };
      if (field === 'resourceCategory') next.resourceCategoryLabel = pickedLabel;
      if (field === 'resourceSkills') next.resourceSkillsLabel = pickedLabel;
      return next;
    });
  }, []);

  const handleSaveEffortRow = useCallback(() => {
    if (!currentEffort.resourceCategory) {
      toast.error('Please select Resource Category');
      return;
    }
    if (!currentEffort.resourceSkills) {
      toast.error('Please select Resource Skills');
      return;
    }
    if (!currentEffort.effortHrs || parseFloat(currentEffort.effortHrs) <= 0) {
      toast.error('Please enter valid Effort Hours');
      return;
    }

    const newCategory = Number(currentEffort.resourceCategory);
    const newSkills = Number(currentEffort.resourceSkills);
    const combo = `${newCategory}__${newSkills}`;

    const isDuplicate = effortRows.some(
      (r) => r.id !== editingRowId && `${r.resourceCategory}__${r.resourceSkills}` === combo,
    );

    if (isDuplicate) {
      toast.warn('This Resource Category + Skill combination already exists.');
      return;
    }

    setEffortRows((prev) => {
      if (editingRowId) {
        return prev.map((r) =>
          r.id === editingRowId
            ? {
              ...r,
              resourceCategory: newCategory,
              resourceCategoryLabel: currentEffort.resourceCategoryLabel ?? r.resourceCategoryLabel,
              resourceSkills: newSkills,
              resourceSkillsLabel: currentEffort.resourceSkillsLabel ?? r.resourceSkillsLabel,
              effortHrs: currentEffort.effortHrs,
              details: currentEffort.details ?? '',
            }
            : r,
        );
      }
      return [
        ...prev,
        {
          id: `${Date.now()}-${Math.random()}`,
          resourceCategory: newCategory,
          resourceCategoryLabel: currentEffort.resourceCategoryLabel ?? '',
          resourceSkills: newSkills,
          resourceSkillsLabel: currentEffort.resourceSkillsLabel ?? '',
          effortHrs: currentEffort.effortHrs,
          details: currentEffort.details ?? '',
        },
      ];
    });

    setCurrentEffort(emptyEffortForm());
    setEditingRowId(null);
  }, [currentEffort, editingRowId, effortRows]);

  const handleEditEffortRow = useCallback((row) => {
    setCurrentEffort({
      resourceCategory: row.resourceCategory,
      resourceCategoryLabel: row.resourceCategoryLabel ?? '',
      resourceSkills: row.resourceSkills,
      resourceSkillsLabel: row.resourceSkillsLabel ?? '',
      effortHrs: row.effortHrs,
      details: row.details ?? '',
    });
    setEditingRowId(row.id);
  }, []);

  const handleDeleteEffortRow = useCallback((rowId) => {
    setEffortRows((prev) => prev.filter((r) => r.id !== rowId));
    if (editingRowId === rowId) {
      setCurrentEffort(emptyEffortForm());
      setEditingRowId(null);
    }
  }, [editingRowId]);

  const isAddEffortDisabled = useMemo(() => {
    const isMainFormInvalid
    = !form.projectName
    || !form.crName?.trim()
    || !form.crDescription?.trim()
    || !form.effortsInvestment
    || !form.customerApprovalStatus;

    const isEffortRowInvalid
    = !currentEffort.resourceCategory
    || !currentEffort.resourceSkills
    || !currentEffort.effortHrs
    || parseFloat(currentEffort.effortHrs) <= 0;

    return submitting || isMainFormInvalid || isEffortRowInvalid;
  }, [form, currentEffort, submitting]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.crName.trim()) return toast.error('CR Name required');
    if (form.crName.length > LIMITS.CR_NAME) return toast.error('CR Name max 100');
    if (form.crDescription.length > LIMITS.DESCRIPTION) return toast.error('Description max 250');
    if (form.remarks.length > LIMITS.REMARKS) return toast.error('Remarks max 200');

    if (!form.customerApprovalStatus) {
      toast.error('Customer Approval Status is mandatory');
      return;
    }

    if (!effortRows.length) {
      toast.error('At least one effort estimation row is required.');
      return;
    }

    const effortRowsPayload = effortRows.map((row) => ({
      category_id: Number(row.resourceCategory) || null,
      type_id: Number(row.resourceSkills) || null,
      efforts_hours: parseFloat(row.effortHrs) || 0,
      details: row.details,
    }));

    const userPayload = {
      employee_no: userProfile?.id,
      updated_by: userProfile?.id || '',
      employeeId: userProfile?.employeeId || '',
    };

    const rawProjectId = form.projectName || rowData?.projectId || rowData?.project_id;
    const parsedProjectId = rawProjectId != null && rawProjectId !== '' ? parseInt(rawProjectId, 10) : null;
    const projectIdInt = Number.isNaN(parsedProjectId) ? null : parsedProjectId;

    const payload = isCreate
      ? {
        cr_number: '',
        requester_name: userProfile?.name || '',
        project_type: rowData?.projectType || '',
        account_manager: rowData?.accountManager || '',
        efforts_hour: totalEffort,
        efforts_investment: form.effortsInvestment,
        remarks: form.remarks,
        approve_date: null,
        account_name: form.crName,
        project_id: projectIdInt,
        project_name: rowData?.projectName || '',
        project_description: form.crDescription,
        project_category: rowData?.projectCategory || '',
        effort_type: 'H',
        cr_status_id: 1,
        customer_approved_status: form.customerApprovalStatus,
        requester_id: userProfile?.employeeId || userProfile?.id,
        effort_rows: effortRowsPayload,
        user: userPayload,
      }
      : {
        cr_id: rowData?.cr_id ?? rowData?.crId,
        project_id: projectIdInt,
        account_name: form.crName,
        description: form.crDescription,
        effort_hour: totalEffort,
        einv: form.effortsInvestment,
        remarks: form.remarks,
        customer_approved_status: form.customerApprovalStatus,
        dropped_status: form.droppedStatus === 'Dropped' ? 3 : undefined,
        created_by: userProfile?.employeeId || userProfile?.id || '',
        effort_rows: effortRowsPayload,
        user: userPayload,
      };

    setSubmitting(true);
    try {
      const crId = rowData?.cr_id ?? rowData?.crId;
      const response = isCreate
        ? await createChangeRequest({ authToken: token, payload })
        : await updateChangeRequest({ authToken: token, crId, payload });

      const json = response?.data;

      if (json?.success || response?.status < 400) {
        toast.success(
          isCreate
            ? 'Change Request created successfully'
            : 'Change Request updated successfully',
        );
        if (typeof onSuccess === 'function') {
          onSuccess();
        } else {
          onHide();
        }
      } else {
        toast.error(json?.message || 'Something went wrong');
      }
    } catch {
      toast.error('Server error. Please try again');
    } finally {
      setSubmitting(false);
    }
  };

  const dialogHeader = isCreate ? 'Add Change Request' : 'Edit Change Request';

  const crStatusOptions = (() => {
    const current = rowData?.crStatus ?? crDetail?.cr_status_value ?? '';
    const opts = [{ value: current, label: current }];
    if (current.toLowerCase() !== 'dropped') {
      opts.push({ value: 'Dropped', label: 'Dropped' });
    }
    return opts;
  })();

  const isChanged = useMemo(() => {
    if (!initialState || isCreate) return true;
    const current = getCurrentState();
    return JSON.stringify(current) !== JSON.stringify(initialState);
  }, [form, effortRows, initialState, isCreate]);

  const isLoading = optionsLoading || (!isCreate && crDetailLoading);

  return (
    <>
      {submitting
        && createPortal(
          <div className="cr-global-overlay">
            <Loader
              text={isCreate ? 'Creating Change Request…' : 'Updating Change Request…'}
            />
          </div>,
          document.body,
        )}

      <Dialog
        header={dialogHeader}
        visible={visible}
        onHide={onHide}
        maskClassName="cr-edit-dialog-mask"
        className="cr-edit-dialog"
        modal
        draggable={false}
        resizable={false}
        blockScroll
        dismissableMask
        appendTo={document.body}
      >
        {(isCreate || rowData)
          && (isLoading ? (
            <DialogSkeleton />
          ) : (
            <form className="cr-edit-dialog__form" onSubmit={handleSubmit}>
              <div className="cr-edit-dialog__body">
                <div className="cr-edit-dialog__inputs-wrapper">
                  {!isCreate && (
                    <div className="cr-edit-dialog__info-row">
                      <div className="cr-edit-dialog__info-card">
                        <span className="cr-edit-dialog__info-label">Project Name</span>
                        <span className="cr-edit-dialog__info-value">
                          {crDetail?.project_name ?? rowData?.projectName ?? rowData?.projectId ?? '-'}
                        </span>
                      </div>

                      <div className="cr-edit-dialog__info-card">
                        <span className="cr-edit-dialog__info-label">CR Number</span>
                        <span className="cr-edit-dialog__info-value">
                          {crDetail?.cr_number ?? rowData?.crNumber ?? rowData?.crId ?? '-'}
                        </span>
                      </div>

                      <div className="cr-edit-dialog__info-card">
                        <span className="cr-edit-dialog__info-label">CR Status</span>
                        <span className="cr-edit-dialog__info-value">
                          {crDetail?.cr_status_value ?? rowData?.crStatus ?? '-'}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="cr-edit-dialog__row">
                    <div className="cr-edit-dialog__field">
                      <label className="cr-edit-dialog__label">
                        Project Name <span style={{ color: 'red' }}>*</span>
                      </label>
                      <SearchDropdown
                        options={projectNameOptions}
                        placeholder="Select Project"
                        value={projectNameDropdownValue}
                        onChange={(e) => handleFormChange('projectName', e.target.value)}
                        dropdownCss="cr-edit-dialog_search-dropdown"
                        disabled={isProjectDisabled}
                      />
                    </div>

                    <div className="cr-edit-dialog__field">
                      <label className="cr-edit-dialog__label">
                        CR Name <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        className="cr-edit-dialog__input"
                        value={form.crName}
                        onChange={(e) => handleFormChange('crName', e.target.value)}
                        required
                        maxLength={LIMITS.CR_NAME}
                        placeholder="Enter Change Request Name"
                        disabled={submitting}
                      />
                    </div>

                    <div className="cr-edit-dialog__field">
                      <label className="cr-edit-dialog__label">
                        CR Description <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        className="cr-edit-dialog__input"
                        value={form.crDescription}
                        onChange={(e) => handleFormChange('crDescription', e.target.value)}
                        required
                        maxLength={LIMITS.DESCRIPTION}
                        placeholder="Enter Change Request Description"
                        disabled={submitting}
                      />
                    </div>

                    <div className="cr-edit-dialog__field">
                      <label className="cr-edit-dialog__label">Remarks</label>
                      <input
                        className="cr-edit-dialog__input"
                        value={form.remarks}
                        onChange={(e) => handleFormChange('remarks', e.target.value)}
                        placeholder="Remarks (optional)"
                        maxLength={LIMITS.REMARKS}
                        disabled={submitting}
                      />
                    </div>

                    <div className="cr-edit-dialog__field">
                      <label className="cr-edit-dialog__label"
                        style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Efforts Investment <span style={{ color: 'red' }}>*</span>

                        <Tooltip
                          title={
                            <span className='cr-tool-tip-text' style={{ fontSize: '11px', lineHeight: '1.5' }}>
                              Select &quot;No&quot; if the CR is billed to the customer.<br />
                              Select &quot;Yes&quot; if the CR is not billed to the customer.
                            </span>
                          }
                          placement="right"
                          slotProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: '#ffffff',
                                color: '#000000',
                                boxShadow: '0px 2px 10px rgba(0,0,0,0.15)',
                                border: '1px solid #e0e0e0',
                                width: '100%',
                                maxWidth: '250px',
                                padding: '10px 12px',
                              },
                            },
                          }}
                        >
                          <IconButton
                            aria-label="info"
                            sx={{ padding: '2px', marginLeft: '2px' }}
                          >
                            <InfoOutlinedIcon sx={{ width: '16px', height: '16px', color: '#666' }} />
                          </IconButton>
                        </Tooltip>
                      </label>
                      <SearchDropdown
                        options={effortInvestmentOptions}
                        placeholder="Select"
                        value={effortsInvestmentDropdownValue}
                        onChange={(e) => handleFormChange('effortsInvestment', e.target.value)}
                        dropdownCss="cr-edit-dialog_search-dropdown"
                        searchBox={false}
                        disabled={submitting || optionsLoading}
                      />
                    </div>

                    <div className="cr-edit-dialog__field">
                      <label className="cr-edit-dialog__label">
                        Customer Approval Status <span style={{ color: 'red' }}>*</span>
                      </label>
                      <SearchDropdown
                        options={filteredCustomerApprovalOptions}
                        placeholder="Select"
                        value={customerApprovalDropdownValue}
                        onChange={(e) => handleFormChange('customerApprovalStatus', e.target.value)}
                        dropdownCss="cr-edit-dialog_search-dropdown"
                        searchBox={false}
                        disabled={submitting || optionsLoading}
                      />
                    </div>

                    {!isCreate && crStatusVisible && (
                      <div className="cr-edit-dialog__field">
                        <label className="cr-edit-dialog__label">CR Status</label>
                        <SearchDropdown
                          options={crStatusOptions}
                          placeholder="Select"
                          value={resolveDropdownValue(crStatusOptions, form.droppedStatus)}
                          onChange={(e) => handleFormChange('droppedStatus', e.target.value)}
                          dropdownCss="cr-edit-dialog_search-dropdown"
                          searchBox={false}
                          disabled={submitting}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="cr-edit-effort-estimation__wrapper">
                  <div className="cr-edit-dialog__section-title">Effort Estimation</div>

                  <div className="cr-edit-dialog__effort-form">
                    <div className="cr-edit-dialog__effort-grid">
                      <div className="cr-edit-dialog__field">
                        <label className="cr-edit-dialog__label">Resource Category</label>
                        <SearchDropdown
                          options={resourceCategoryOptions}
                          placeholder="Select"
                          value={resourceCategoryDropdownValue}
                          onChange={(e) => handleCurrentEffortChange('resourceCategory', e.target.value)}
                          dropdownCss="cr-edit-dialog_search-dropdown"
                          disabled={submitting || optionsLoading}
                        />
                      </div>

                      <div className="cr-edit-dialog__field">
                        <label className="cr-edit-dialog__label">Resource Skills</label>
                        <SearchDropdown
                          options={resourceSkillsOptions}
                          placeholder="Select"
                          value={resourceSkillsDropdownValue}
                          onChange={(e) => handleCurrentEffortChange('resourceSkills', e.target.value)}
                          dropdownCss="cr-edit-dialog_search-dropdown"
                          disabled={submitting || optionsLoading}
                        />
                      </div>

                      <div className="cr-edit-dialog__field">
                        <label className="cr-edit-dialog__label">Effort (Hrs)</label>
                        <input
                          className="cr-edit-dialog__input"
                          type="number"
                          min="0"
                          max={LIMITS.EFFORT_HOURS_MAX}
                          step="0.01"
                          value={currentEffort.effortHrs}
                          onChange={(e) => handleCurrentEffortChange('effortHrs', e.target.value)}
                          placeholder="Enter hours"
                          disabled={submitting}
                        />
                      </div>

                      <div className="cr-edit-dialog__field">
                        <label className="cr-edit-dialog__label">Details</label>
                        <input
                          className="cr-edit-dialog__input"
                          value={currentEffort.details}
                          maxLength={LIMITS.DETAILS}
                          onChange={(e) => handleCurrentEffortChange('details', e.target.value)}
                          placeholder="Enter details"
                          disabled={submitting}
                        />
                      </div>
                    </div>

                    <div className="cr-edit-dialog__effort-estimation-btn-wrapper">
                      <button
                        type="button"
                        className="cr-edit-dialog__btn cr-edit-dialog__btn--save-row"
                        onClick={handleSaveEffortRow}
                        disabled={isAddEffortDisabled}
                      >
                        {editingRowId ? 'Update' : 'Add'}
                      </button>
                      {editingRowId && (
                        <button
                          type="button"
                          className="cr-edit-dialog__btn cr-edit-dialog__btn--cancel-row"
                          onClick={() => {
                            setCurrentEffort(emptyEffortForm());
                            setEditingRowId(null);
                          }}
                          disabled={isAddEffortDisabled}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="cr-edit-dialog__effort-table-wrapper">
                    <table className="cr-edit-dialog__effort-table">
                      <thead>
                        <tr>
                          <th>Resource Category</th>
                          <th>Resource Skills</th>
                          <th>Efforts (Hrs)</th>
                          <th>Details</th>
                          <th className="cr-align-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {effortRows.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="cr-empty-table">
                              No effort estimation rows added.
                            </td>
                          </tr>
                        ) : (
                          effortRows.map((row) => {
                            const isEditing = editingRowId === row.id;
                            return (
                              <tr key={row.id} className={isEditing ? 'is-editing-row' : ''}>
                                <td>{getOptionLabel(resourceCategoryOptions, row.resourceCategory, row.resourceCategoryLabel)}</td>
                                <td>{getOptionLabel(resourceSkillsOptions, row.resourceSkills, row.resourceSkillsLabel)}</td>
                                <td>{parseFloat(row.effortHrs || 0).toFixed(2)}</td>
                                <td>{row.details || '-'}</td>
                                <td className="cr-align-center">
                                  {isEditing ? (
                                    <span className="cr-editing-tag">Editing...</span>
                                  ) : (
                                    <div className="cr-table-actions">
                                      <button
                                        type="button"
                                        className="cr-action-icon-btn cr-action-icon-btn--edit"
                                        onClick={() => handleEditEffortRow(row)}
                                        disabled={submitting}
                                        title="Edit"
                                      >
                                        <EditOutlinedIcon fontSize="small" />
                                      </button>
                                      <button
                                        type="button"
                                        className="cr-action-icon-btn cr-action-icon-btn--delete"
                                        onClick={() => handleDeleteEffortRow(row.id)}
                                        disabled={submitting}
                                        title="Delete"
                                      >
                                        <DeleteOutlineIcon fontSize="small" />
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                      {effortRows.length > 0 && (
                        <tfoot>
                          <tr className="cr-effort-total-row">
                            <td className="cr-effort-total-cell"></td>
                            <td className="cr-effort-total-cell"></td>
                            <td className="cr-effort-total-cell">
                              <div className="total-cell">
                                <span className="total-label">Total:</span>
                                <span className="total-value">{totalEffort.toFixed(2)}</span>
                              </div>
                            </td>
                            <td className="cr-effort-total-cell"></td>
                            <td className="cr-effort-total-cell"></td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </div>

              <div className="cr-edit-dialog__footer">
                <div className="cr-edit-dialog__footer-button-wrapper">
                  <button
                    type="button"
                    className="cr-edit-dialog__btn cr-edit-dialog__btn--cancel"
                    onClick={onHide}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="cr-edit-dialog__btn cr-edit-dialog__btn--submit"
                    disabled={submitting || (!isCreate && !isChanged)}
                  >
                    {submitting ? (
                      <span className="cr-btn-loader" />
                    ) : (
                      'Submit'
                    )}
                  </button>
                </div>
              </div>
            </form>
          ))}
      </Dialog>
    </>
  );
};

ChangeRequestDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  rowData: PropTypes.object,
  editFormOptionsBaseURL: PropTypes.string,
  crDetailBaseURL: PropTypes.string,
  isCreate: PropTypes.bool,
  onSuccess: PropTypes.func,
};

export default ChangeRequestDialog;
